import { InternalGrade } from "../types/api/collaborators.type";
import { ConfigItem } from "../types/api/configurations.type";
import {
  OrgAreaItem,
  OrgCCItem,
  OrgCmsheet,
  OrgPracticeItem,
  OrgUserRole,
} from "../types/api/org.type";
import { Role } from "../types/AppTypes";

export const findAreaById = (
  org: OrgAreaItem[],
  areaId: string | null,
): OrgAreaItem | null => {
  if (!areaId) return null;

  const area = org.find((area) => area.id === areaId);
  if (!area) {
    return null;
  }

  return area;
};

export const findPracticeById = (
  org: OrgAreaItem[],
  practiceId: string | null,
  areaId?: string,
): OrgPracticeItem | null => {
  if (areaId) {
    const area = findAreaById(org, areaId);
    if (!area) return null;

    const practice = area.practiceList.find(
      (practice) => practice.id === practiceId,
    );

    if (!practice) {
      return null;
    }

    return practice;
  }

  for (const area of org) {
    for (const practice of area.practiceList) {
      if (practice.id === practiceId) {
        return practice;
      }
    }
  }

  return null;
};

export const findCCById = (
  org: OrgAreaItem[],
  ccId: string | null,
  areaId?: string,
  practiceId?: string | null,
): OrgCCItem | null => {
  if (areaId && practiceId) {
    const practice = findPracticeById(org, practiceId, areaId);
    if (!practice) return null;

    const cc = practice.ccList.find((cc) => cc.id === ccId);
    if (!cc) {
      return null;
    }

    return cc;
  }

  for (const area of org) {
    for (const practice of area.practiceList) {
      for (const cc of practice.ccList) {
        if (cc.id === ccId) {
          return cc;
        }
      }
    }
  }
  return null;
};

export const findStatusLabelByValue = (
  org: OrgCmsheet,
  cmSheetStatus: string | null,
): string | null => {
  if (!cmSheetStatus) return null;

  const status = org.status.find((area) => area.value === cmSheetStatus);
  if (!status) {
    return null;
  }

  return status.label;
};

export const findLabelFromValue = (
  options: { label: string; value: string }[],
  value: string,
): string => {
  const option = options.find((option) => option.value === value);
  return option ? option.label : value;
};

export type TargetItem = {
  practice: string;
  cc: string[];
};

export const getPracticeCoverageSummary = (
  org: OrgAreaItem[],
  ccListInput: string[],
): TargetItem[] | null => {
  if (ccListInput.length === 0) {
    return null;
  }

  for (const area of org) {
    const foundPractices: TargetItem[] = [];
    const practiceListWithoutEmptyCC = area.practiceList.filter(
      (practice) => practice.ccList.length > 0,
    );

    for (const practice of practiceListWithoutEmptyCC) {
      const ccIds = practice.ccList.map((cc) => cc.id);
      const hasMatch = ccIds.every((ccId) => ccListInput.includes(ccId));

      if (hasMatch) {
        foundPractices.push({ practice: practice.name, cc: [] });
      } else {
        for (const cc of practice.ccList) {
          if (ccListInput.includes(cc.id)) {
            const existingPractice = foundPractices.find(
              (item) => item.practice === practice.name,
            );
            if (!existingPractice) {
              foundPractices.push({ practice: practice.name, cc: [cc.name] });
            } else {
              existingPractice.cc.push(cc.name);
            }
          }
        }
      }
    }

    const allPracticesMatched =
      foundPractices.length === practiceListWithoutEmptyCC.length;

    if (allPracticesMatched) {
      return [];
    }

    return [...foundPractices];
  }

  return null;
};

export const findLabelRoleByValue = (
  org: OrgUserRole,
  userRoles: Role,
): string | null => {
  if (!userRoles) return null;

  const role = org.role.find((rol) => rol.value === userRoles);

  if (!role) {
    return null;
  }
  return role.label;
};

export const getInternalGradeLabels = (
  configYears: ConfigItem[],
  internalGrade?: InternalGrade | null,
) => {
  const currentYearConfig = configYears.find((item) => item.current)?.config;

  const jobFamilies = internalGrade?.jobFamilyListId?.length
    ? currentYearConfig?.jobFamilies
        .filter((item) => internalGrade?.jobFamilyListId.includes(item.id))
        .map((item) => item.name)
        .join(", ")
    : null;

  const grade = internalGrade?.gradeId
    ? currentYearConfig?.grades.find(
        (item) => internalGrade?.gradeId === item.id,
      )?.name
    : null;

  const pathName = internalGrade?.pathId
    ? currentYearConfig?.paths.find((item) => internalGrade.pathId === item.id)
        ?.name
    : null;

  const pathGradeName = internalGrade?.pathGradeId
    ? currentYearConfig?.pathGrades.find(
        (item) => internalGrade.pathGradeId === item.id,
      )?.name
    : null;

  const path =
    pathName && pathGradeName
      ? `${pathName} - ${pathGradeName}`
      : (pathName ?? pathGradeName ?? null);

  const subGrade = internalGrade?.subgradeId
    ? currentYearConfig?.subgrades.find(
        (item) => internalGrade.subgradeId === item.id,
      )?.name
    : null;

  return { jobFamilies, grade, path, subGrade };
};
